/**
 * verse/discourse.ts — Discourse Arc Threading
 *
 * Maintains a ring buffer of 7 kernel phrases from both speakers,
 * enabling cross-turn semantic matching. When the current kernel
 * echoes a previous one (cosine > 0.6), the EVOLVE operator fires
 * and the Aeterna can weave temporal continuity: "you keep coming
 * back to that", "earlier you said X, and now..."
 *
 * This is what makes the Aeterna feel like it's TRACKING you —
 * not just responding to this message, but to the arc of the
 * conversation.
 */

import { cosine } from "../neural/math.js";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ConversationArc {
  /** Dominant emotional territory across recent turns */
  territory: "grief" | "joy" | "conflict" | "exploration" | "intimacy" | "neutral";
  /** Are we in an ongoing narrative? (3+ turns on same emotional territory) */
  sustained: boolean;
  /** The relational subject threading through recent turns (e.g., "her", "mom") */
  threadedSubject: string | null;
  /** Number of consecutive turns in this territory */
  depth: number;
}

export interface DiscourseEntry {
  kernel: string;
  embedding: Float32Array;
  speaker: "player" | "aeterna";
  turn: number;
  timestamp: number;
}

export interface DiscourseEcho {
  /** The previous entry that echoes */
  entry: DiscourseEntry;
  /** Cosine similarity to current */
  similarity: number;
  /** How many turns ago */
  turnsAgo: number;
}

// ─── Discourse Buffer ───────────────────────────────────────────────────────

const MAX_BUFFER = 7;
const ECHO_THRESHOLD = 0.6;

let buffer: DiscourseEntry[] = [];
let turnCounter = 0;

/**
 * Push a kernel into the discourse buffer.
 * Called after kernel extraction for each turn.
 */
export function pushKernel(
  kernel: string,
  embedding: Float32Array,
  speaker: "player" | "aeterna",
): void {
  buffer.push({
    kernel,
    embedding,
    speaker,
    turn: turnCounter++,
    timestamp: Date.now(),
  });
  if (buffer.length > MAX_BUFFER) buffer.shift();
}

/**
 * Find a previous player kernel that semantically echoes the current one.
 * Returns the strongest echo above threshold, or null.
 *
 * Only looks at player kernels (not aeterna's own) to detect when
 * the player keeps circling back to the same territory.
 */
export function findEcho(currentEmbedding: Float32Array): DiscourseEcho | null {
  let best: DiscourseEcho | null = null;

  for (const entry of buffer) {
    if (entry.speaker !== "player") continue;
    // Don't match the most recent entry (that's the current turn)
    if (entry.turn === turnCounter - 1) continue;

    const sim = cosine(currentEmbedding, entry.embedding);
    if (sim > ECHO_THRESHOLD && (!best || sim > best.similarity)) {
      best = {
        entry,
        similarity: sim,
        turnsAgo: turnCounter - entry.turn,
      };
    }
  }
  return best;
}

/**
 * Get all kernels from the buffer (for context).
 */
export function getRecentKernels(): ReadonlyArray<DiscourseEntry> {
  return buffer;
}

/**
 * Get the last player kernel (for anti-echo in responses).
 */
export function getLastPlayerKernel(): string | null {
  for (let i = buffer.length - 1; i >= 0; i--) {
    if (buffer[i].speaker === "player") return buffer[i].kernel;
  }
  return null;
}

/**
 * Get the last aeterna kernel (for self-awareness).
 */
export function getLastAeternaKernel(): string | null {
  for (let i = buffer.length - 1; i >= 0; i--) {
    if (buffer[i].speaker === "aeterna") return buffer[i].kernel;
  }
  return null;
}

// ─── Named Entity Store ────────────────────────────────────────────────────
// Tracks people, places, and things the player mentions by name.
// This is what makes "Who is John?" answerable after "my friend John is cool."

export interface NamedEntity {
  /** The proper noun (e.g., "John") */
  name: string;
  /** Relationship to the player ("friend", "brother", etc.) or null */
  relationship: string | null;
  /** Descriptors the player has given ("super cool", "works hard") */
  descriptors: string[];
  /** Turn number of first mention */
  firstMention: number;
  /** Turn number of last mention */
  lastMention: number;
}

const namedEntities = new Map<string, NamedEntity>();

// Patterns for extracting proper nouns in relational context
const RE_INTRO = /\b(?:my\s+)?(?:friend|brother|sister|mom|dad|mother|father|husband|wife|partner|colleague|coworker|boss|son|daughter|uncle|aunt|cousin|grandma|grandpa|girlfriend|boyfriend)\s+([A-Z][a-z]+)\b/;
// Reverse pattern: '[ProperNoun] is my [relation]' — most natural introduction form
const RE_INTRO_REVERSE = /\b([A-Z][a-z]+)\s+(?:is|was)\s+my\s+(?:friend|brother|sister|mom|dad|mother|father|husband|wife|partner|colleague|coworker|boss|son|daughter|uncle|aunt|cousin|grandma|grandpa|girlfriend|boyfriend)\b/i;
const RE_NAMED = /\b(?:named|called|name is|name's|this is|meet)\s+([A-Z][a-z]+)\b/i;
const RE_RELATIONSHIP = /\b(my\s+)?(friend|brother|sister|mom|dad|mother|father|husband|wife|partner|colleague|coworker|boss|son|daughter|uncle|aunt|cousin|grandma|grandpa|girlfriend|boyfriend)\b/i;

/**
 * Extract proper nouns from text. A proper noun is a capitalized word
 * that isn't at the sentence start and isn't a common English word.
 */
const COMMON_CAPS = new Set([
  "I", "I'm", "I'll", "I've", "I'd",
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
  "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December",
  "The", "A", "An", "He", "She", "It", "We", "They", "My", "Your",
  "This", "That", "What", "Who", "How", "Why", "When", "Where",
  "Yeah", "Yes", "No", "Hey", "Hello", "Hi", "Okay", "OK",
  "Did", "Do", "Is", "Are", "Was", "Were", "Will", "Would", "Could",
  "But", "And", "Or", "So", "If", "Not", "Also", "Just", "Really",
]);

function extractProperNouns(text: string): string[] {
  const words = text.split(/\s+/);
  const names: string[] = [];
  for (let i = 0; i < words.length; i++) {
    const word = words[i].replace(/[.,!?;:'"]+$/g, "");
    if (!word || word.length < 2) continue;
    // Must be capitalized
    if (!/^[A-Z][a-z]+$/.test(word)) continue;
    // Skip common capitalized words
    if (COMMON_CAPS.has(word)) continue;
    // Skip sentence-start words UNLESS followed by a predicate verb
    // ("John is really smart" → "John" is a proper noun, not sentence-start noise)
    if (i === 0) {
      const nextWord = words[1]?.replace(/[.,!?;:'"]+$/g, "");
      if (nextWord && /^(?:is|was|works|does|makes|builds|plays|loves|has|creates|knows|lives|studies|codes|designs|writes|teaches|are|were|seems|gets|runs|started|became)(?:'s|'d|'ll|'ve)?$/i.test(nextWord)) {
        // Sentence-start name followed by predicate — this is an entity mention
      } else {
        continue;
      }
    }
    const prev = words[i - 1];
    if (prev && /[.!?]$/.test(prev)) continue;
    names.push(word);
  }
  return names;
}

/**
 * Extract descriptive phrases about a person from the text.
 * Looks for adjectives and descriptive clauses near the person's name.
 */
function extractDescriptors(text: string, name: string): string[] {
  const descs: string[] = [];

  // Match against ORIGINAL text to preserve casing (VR, Game Engine, etc.)
  // "X is/are [desc]" patterns
  const isMatch = new RegExp(`${name}(?:'s|\\s+is|\\s+was|\\s+seems)\\s+(.+?)(?:[.,!]|$)`, "i");
  const m = text.match(isMatch);
  if (m) {
    // Split compound predicates into atomic descriptors
    const atoms = splitCompoundDescriptor(m[1]);
    for (const atom of atoms) {
      const cleaned = cleanDescriptor(atom);
      if (cleaned.length > 2) descs.push(cleaned);
    }
  }

  // "super/really/very [adj]" near the name
  const superMatch = text.match(/(?:super|really|very|incredibly|insanely|pretty)\s+(\w+)/i);
  if (superMatch) descs.push(cleanDescriptor(superMatch[0]));

  // "he/she/they [verb]" patterns in same message (for mixed messages like "my friend John, he codes all day")
  const pronounMatch = text.match(/\b(?:he|she|they)\s+(\w+s?\s+.{5,40}?)(?:[.,!]|$)/i);
  if (pronounMatch) descs.push(cleanDescriptor(pronounMatch[1]));

  // Deduplicate before returning — multiple matchers can find the same phrase
  const seen = new Set<string>();
  return descs.filter(d => {
    if (d.length <= 2) return false;
    const key = d.toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ─── Pronoun Coreference ──────────────────────────────────────────────────
// Tracks the most recently mentioned entity so "He/She/They" can resolve.
// Simple recency-based — the last named entity is the antecedent.

let lastMentionedEntityName: string | null = null;

const RE_PRONOUN_SUBJECT = /^(he|she|they)\b/i;
const RE_PRONOUN_MID = /\b(he|she|they)\b/i;

/**
 * Resolve which entity a pronoun-led message refers to.
 * Returns the entity name or null.
 */
export function resolvePronouns(text: string): string | null {
  if (!RE_PRONOUN_SUBJECT.test(text.trim()) && !RE_PRONOUN_MID.test(text)) return null;
  return lastMentionedEntityName;
}

/**
 * Get the last mentioned entity name (for external use).
 */
export function getLastMentionedEntity(): string | null {
  return lastMentionedEntityName;
}

/**
 * Clean a descriptor to be ABOUT the entity, not about the player.
 * "a superhuman friend of mine" → "superhuman"
 * "works as hard as I do" → "works incredibly hard"
 * Strips first-person references, filler, and relational framing.
 */
function cleanDescriptor(desc: string): string {
  let d = desc.trim();
  // Strip leading copula: "is my baby girl" → "my baby girl"
  d = d.replace(/^(?:is|are|was|were|seems?|looks?)\s+/i, "").trim();
  // Replace "as [adj] as I [verb]..." → "incredibly [adj]" (BEFORE stripping "I do")
  d = d.replace(/as\s+(\w+)\s+as\s+I\s+\w+.*/i, "incredibly $1").trim();
  // Strip "of mine" / "of ours"
  d = d.replace(/\s+of\s+(?:mine|ours|my)\b/gi, "").trim();
  // Strip trailing "I do" / "I am" / "I have" / "you know"
  d = d.replace(/\s*,?\s*(?:I\s+(?:do|am|have|did|was)|you\s+know)\s*$/i, "").trim();
  // Strip "my friend" / "my brother" etc. from front — relationship, not descriptor
  d = d.replace(/^(?:my\s+)?(?:friend|brother|sister|mom|dad|husband|wife|partner)\s*/i, "").trim();
  // Strip first-person clauses: "and I call her my kitten" / "and I love her"
  d = d.replace(/\s*(?:,?\s*and)?\s+(?:I|we)\s+(?:call|named?|think|say|love|like|adore|consider)\s+(?:her|him|them|it)\s+.*/i, "").trim();
  // Strip trailing punctuation: commas, periods, exclamation, semicolons
  d = d.replace(/[,;:.!?]+$/, "").trim();
  // Strip leading punctuation artifacts
  d = d.replace(/^[,;:.!?]+\s*/, "").trim();
  // Collapse whitespace
  d = d.replace(/\s+/g, " ");
  return d.slice(0, 80);
}

/**
 * Split a compound predicate into atomic descriptors.
 * "super beautiful and cute and chubby and I call her my kitten"
 * → ["super beautiful", "cute", "chubby"]  (I call her... stripped by cleanDescriptor)
 *
 * Splits on "and" ONLY when both sides look like adjectives/simple descriptors.
 * Does NOT split compound noun phrases: "VR and Game Engine" stays as one.
 */
function splitCompoundDescriptor(raw: string): string[] {
  // First: strip first-person clauses before splitting
  const stripped = raw.replace(/\s*(?:,?\s*and)?\s+(?:I|we)\s+(?:call|named?|think|say|love|like|adore|consider)\s+(?:her|him|them|it)\s+.*/i, "").trim();

  // Split on " and " — but only produce atomic descriptors.
  const parts = stripped.split(/\s+and\s+/i);
  if (parts.length <= 1) return [stripped];

  // Heuristic: if most parts are short (1-3 words), treat as adjective list.
  // If parts are long or contain verbs, don't split (it's a compound noun phrase).
  const shortCount = parts.filter(p => p.trim().split(/\s+/).length <= 4).length;
  if (shortCount >= parts.length * 0.6) {
    // Adjective list — return each part as a separate descriptor
    return parts.map(p => p.trim()).filter(p => p.length > 1);
  }

  // Not a clean adjective list — return as-is
  return [stripped];
}

/**
 * Extract descriptors from pronoun-led sentences.
 * Handles MULTIPLE clauses: "He works hard, but he zooms on different topics"
 * produces both "works incredibly hard" and "zooms on different topics".
 */
function extractPronounDescriptors(text: string): string[] {
  const descs: string[] = [];

  // Split on conjunctions/commas to get multiple clauses, but ONLY on
  // conjunction + pronoun restart (", but he" / ", and she") — not bare "and"
  // which splits "VR and Game Engine" into fragments.
  // "He works hard, but he zooms on different topics" → 2 clauses
  // "He's a Ninja with VR and Game Engine tech" → 1 clause (no split)
  const clauses = text.split(/\s*(?:,\s*(?:but|and|plus|also)\s+(?=(?:he|she|they)\b)|\s+but\s+(?=(?:he|she|they)\b))\s*/i)
    .filter(c => c.trim().length > 3);

  for (const clause of clauses) {
    // Extract pet names / nicknames: "I call her my kitten" → "called my kitten"
    const petNameMatch = clause.match(/\bI\s+call\s+(?:her|him|them)\s+(?:my\s+)?(.+?)(?:[.!,]|$)/i);
    if (petNameMatch) {
      const petName = petNameMatch[1].trim();
      if (petName.length > 1 && petName.length < 30) descs.push(`called ${petName}`);
    }

    // "He/She/They is/are/was [desc]" or "He's [desc]"
    const isMatch = clause.match(/\b(?:he|she|they)(?:'s|'re|\s+(?:is|are|was|were))\s+(.+?)(?:[.!]|$)/i);
    if (isMatch) {
      // Split compound predicates: "beautiful and cute and chubby" → 3 descriptors
      const atoms = splitCompoundDescriptor(isMatch[1]);
      for (const atom of atoms) {
        const cleaned = cleanDescriptor(atom);
        if (cleaned.length > 2) descs.push(cleaned);
      }
      continue;
    }

    // "He/She/They [verb] [object]"
    const verbMatch = clause.match(/\b(?:he|she|they)\s+((?:works?|plays?|loves?|does|makes?|builds?|creates?|knows?|lives?|goes|runs?|teaches?|studies|codes?|designs?|writes?|zooms?|focuses|specializes|excels)\s+.+?)(?:[.!]|$)/i);
    if (verbMatch) {
      const cleaned = cleanDescriptor(verbMatch[1]);
      if (cleaned.length > 2) descs.push(cleaned);
    }
  }

  return descs;
}

/**
 * Process a player message for named entities.
 * Call this for every player message, before kernel extraction.
 */
export function processEntities(text: string): void {
  // Try structured introduction patterns first
  const introMatch = text.match(RE_INTRO) ?? text.match(RE_INTRO_REVERSE) ?? text.match(RE_NAMED);
  const relationship = text.match(RE_RELATIONSHIP)?.[2]?.toLowerCase() ?? null;

  if (introMatch) {
    const name = introMatch[1];
    lastMentionedEntityName = name;
    const existing = namedEntities.get(name);
    const descs = extractDescriptors(text, name);
    if (existing) {
      existing.lastMention = turnCounter;
      addDescriptors(existing, descs);
      if (relationship && !existing.relationship) existing.relationship = relationship;
    } else {
      namedEntities.set(name, {
        name,
        relationship,
        descriptors: descs,
        firstMention: turnCounter,
        lastMention: turnCounter,
      });
    }
    return;
  }

  // Determine if this sentence is pronoun-led ("He works hard" / "She's amazing")
  const startsWithPronoun = RE_PRONOUN_SUBJECT.test(text.trim());
  const priorEntity = lastMentionedEntityName;

  if (startsWithPronoun && priorEntity) {
    // ── Pronoun coreference path ──
    // "He's a Ninja with VR" → all information belongs to the prior entity (John).
    // Do NOT create new entities for proper nouns in this sentence (Ninja, Game, Engine
    // are descriptive, not people). Extract multi-clause descriptors and store on the
    // referenced entity.
    const entity = namedEntities.get(priorEntity);
    if (entity) {
      const descs = extractPronounDescriptors(text);
      addDescriptors(entity, descs);
    }
  } else {
    // ── Bare proper noun path ──
    // No pronoun lead — proper nouns ARE potential entities.
    const properNouns = extractProperNouns(text);
    for (const name of properNouns) {
      lastMentionedEntityName = name;
      const existing = namedEntities.get(name);
      const descs = extractDescriptors(text, name);
      if (existing) {
        existing.lastMention = turnCounter;
        addDescriptors(existing, descs);
      } else {
        namedEntities.set(name, {
          name,
          relationship,
          descriptors: descs,
          firstMention: turnCounter,
          lastMention: turnCounter,
        });
      }
    }
  }
}

/**
 * Add descriptors to an entity, deduplicating against existing ones.
 * Uses lowercased comparison to avoid "super cool" and "Super cool" both stored.
 */
function addDescriptors(entity: NamedEntity, newDescs: string[]): void {
  const existing = new Set(entity.descriptors.map(d => d.toLowerCase().trim()));
  for (const d of newDescs) {
    const key = d.toLowerCase().trim();
    if (key.length < 3 || existing.has(key)) continue;
    // Also check for substring containment — "super cool" and "superhuman" are distinct,
    // but "super cool" and "he's super cool" are duplicates
    let isDupe = false;
    for (const e of existing) {
      if (e.includes(key) || key.includes(e)) { isDupe = true; break; }
    }
    if (isDupe) continue;
    existing.add(key);
    entity.descriptors.push(d);
  }
}

/**
 * Look up a named entity. Returns null if unknown.
 */
export function getEntity(name: string): NamedEntity | null {
  return namedEntities.get(name) ?? null;
}

/**
 * Get all known named entities.
 */
export function getEntities(): ReadonlyMap<string, NamedEntity> {
  return namedEntities;
}

/**
 * Get all known proper noun names (for kernel extraction guard).
 */
export function getKnownNames(): ReadonlySet<string> {
  return new Set(namedEntities.keys());
}

// ─── Player Text Buffer (for arc tracking) ─────────────────────────────────

let recentPlayerTexts: string[] = [];
const MAX_TEXTS = 7;

/**
 * Push the full player text into the arc-tracking buffer.
 * Called in the pipeline for every player message (including pre-routed ones)
 * so the conversation arc stays coherent.
 */
export function pushPlayerText(text: string): void {
  recentPlayerTexts.push(text);
  if (recentPlayerTexts.length > MAX_TEXTS) recentPlayerTexts.shift();
}

/**
 * Get recent player texts (for external inspection / testing).
 */
export function getRecentPlayerTexts(): readonly string[] {
  return recentPlayerTexts;
}

// ─── Conversation Arc Tracking ──────────────────────────────────────────────

const GRIEF_WORDS = new Set([
  "passed", "died", "death", "miss", "missing", "lost", "gone", "grief",
  "funeral", "buried", "grave", "mourn", "mourning",
  "cried", "crying", "empty", "quiet", "alone", "lonely",
]);
const JOY_WORDS = new Set([
  "happy", "laugh", "laughing", "smile", "smiling", "joy", "celebrate",
  "love", "beautiful", "wonderful", "amazing",
]);
const CONFLICT_WORDS = new Set([
  "angry", "fight", "fighting", "hurt", "betrayed", "lied", "broken",
  "hate", "furious", "rage",
]);
const INTIMACY_WORDS = new Set([
  "love", "trust", "close", "together", "hold", "holding", "touch",
  "kiss", "safe", "home",
]);
const RELATIONAL_DETECT = new Set([
  "her", "him", "them", "she", "he", "mom", "mother", "dad", "father",
  "brother", "sister", "friend",
]);

/**
 * Analyze the recent player text buffer and return the emotional arc
 * of the conversation — territory, sustained narrative, threaded subject.
 */
export function getConversationArc(): ConversationArc {
  if (recentPlayerTexts.length < 2) {
    return { territory: "neutral", sustained: false, threadedSubject: null, depth: 0 };
  }

  const recent = recentPlayerTexts.slice(-7);

  // Collect all words from recent player texts
  const allWords = recent.flatMap(t => t.toLowerCase().split(/\s+/));

  const griefCount = allWords.filter(w => GRIEF_WORDS.has(w)).length;
  const joyCount = allWords.filter(w => JOY_WORDS.has(w)).length;
  const conflictCount = allWords.filter(w => CONFLICT_WORDS.has(w)).length;
  const intimacyCount = allWords.filter(w => INTIMACY_WORDS.has(w)).length;

  let territory: ConversationArc["territory"] = "neutral";
  let maxCount = 0;

  if (griefCount > maxCount) { territory = "grief"; maxCount = griefCount; }
  if (joyCount > maxCount) { territory = "joy"; maxCount = joyCount; }
  if (conflictCount > maxCount) { territory = "conflict"; maxCount = conflictCount; }
  if (intimacyCount > maxCount) { territory = "intimacy"; maxCount = intimacyCount; }
  if (maxCount < 2) territory = recent.length >= 3 ? "exploration" : "neutral";

  // Detect sustained narrative (3+ turns on same territory)
  const sustained = recent.length >= 3 && maxCount >= 2;

  // Detect threaded relational subject —
  // look for pronouns or relational words that appear across multiple turns
  const relationalCounts = new Map<string, number>();
  for (const text of recent) {
    const words = text.toLowerCase().split(/\s+/);
    const seen = new Set<string>();
    for (const w of words) {
      if (RELATIONAL_DETECT.has(w) && !seen.has(w)) {
        relationalCounts.set(w, (relationalCounts.get(w) ?? 0) + 1);
        seen.add(w);
      }
    }
  }

  let threadedSubject: string | null = null;
  let threadMax = 0;
  for (const [word, count] of relationalCounts) {
    if (count >= 2 && count > threadMax) {
      threadedSubject = word;
      threadMax = count;
    }
  }

  return {
    territory,
    sustained,
    threadedSubject,
    depth: Math.min(recent.length, 7),
  };
}

/**
 * Reset discourse state (for testing or session boundaries).
 */
export function resetDiscourse(): void {
  buffer = [];
  turnCounter = 0;
  recentPlayerTexts = [];
  namedEntities.clear();
  lastMentionedEntityName = null;
}
